<template>
  <ChartCard :data-widget-id="widgetId" :id-for-aria="widgetId">
    <ChartHeader
      :label="widgetLabel"
      :widget-download-id="widgetId"
      tooltip-key="benchmark-range"
    />
    <ChartContent
      height="119px"
      :empty-data="emptyData || noWidgetData"
      :error="error"
      :loading="loading"
      type="horizontalBenchmarkChart"
      class="px-0 pt-4 pb-0"
    >
      <BarChart :id="`${widgetId}-chart`" :data="chartData" :options="chartOptions" :plugins="plugins" title="Offer acceptance rate vs benchmark" />
    </ChartContent>
  </ChartCard>
</template>

<script setup>
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import ChartContent from '@/components/insights/Chart/ChartContent.vue';
import BarChart from '@/components/insights/Chart/BarChart.vue';
import { computed } from 'vue';
import { useColors } from '@/composables/colors';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { CanvasDrawBenchmarkPlugin } from '@/api/helpers/insights/chartPlugins';
import { formatTooltipValue } from '@/helpers/formatters';
import {
  basicChartTooltipPluginConfig,
  hiringReportXScaleOptions,
  benchmarkPluginPercentOffset,
  hiddenYScaleConfig,
} from '@/api/helpers/insights/commonChartConfigs';

const OFFER_ACCEPTANCE_RATE_MAX = 100;
const OFFER_ACCEPTANCE_RATE_BENCHMARK_MIN = 75;
const OFFER_ACCEPTANCE_RATE_BENCHMARK_MAX = 85;

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  emptyData: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const widgetLabel = 'Offer Acceptance Rate vs Benchmark';

const widgetId = 'offer-acceptance-rate-vs-benchmark';

const {
  brandPrimary,
  yellow500,
  textDarkest,
  white,
} = useColors();

const noWidgetData = computed(() => {
  const { offer_acceptance_rate: offerAcceptanceRate } = props.data || {};
  return !offerAcceptanceRate;
});

const chartData = computed(() => {
  const { offer_acceptance_rate: offerAcceptanceRate } = props.data || {};

  return {
    labels: ['Offer Acceptance Rate'],
    datasets: [
      {
        label: '',
        backgroundColor: [
          brandPrimary.value,
          yellow500.value,
        ],
        borderColor: [
          brandPrimary.value,
          yellow500.value,
        ],
        fill: false,
        maxBarThickness: 24,
        barThickness: 'flex',
        data: [
          offerAcceptanceRate,
        ],
      },
    ],
  };
});

const plugins = computed(() => [
  ChartDataLabels,
  CanvasDrawBenchmarkPlugin({
    benchmarkOptions: {
      min: OFFER_ACCEPTANCE_RATE_BENCHMARK_MIN,
      max: OFFER_ACCEPTANCE_RATE_BENCHMARK_MAX,
      ...benchmarkPluginPercentOffset,
    },
    scaleMax: OFFER_ACCEPTANCE_RATE_MAX,
    chartId: `${widgetId}-chart`,
  }),
]);

const chartOptions = computed(() => ({
  maintainAspectRatio: false,
  responsive: true,
  indexAxis: 'y',
  plugins: {
    legend: { display: false },
    tooltip: basicChartTooltipPluginConfig(chartData, widgetId),
    datalabels: {
      color: function colorCallback(context) {
        const max = OFFER_ACCEPTANCE_RATE_MAX;
        const value = context.dataset.data[context.dataIndex];
        const percentOfSpace = (value / max) * 100;

        return percentOfSpace < 20 ? textDarkest.value : white.value;
      },
      anchor: 'end',
      align: function alignCallback(context) {
        const max = OFFER_ACCEPTANCE_RATE_MAX;
        const value = context.dataset.data[context.dataIndex];
        const percentOfSpace = (value / max) * 100;

        if (Number.isNaN(percentOfSpace)) return 'end';

        return percentOfSpace < 20 ? 'end' : 'start';
      },
      formatter: function formatterCallback(value) {
        if (Number.isNaN(value)) return '';
        return formatTooltipValue(value, 'percent');
      },
    },
  },
  scales: {
    x: hiringReportXScaleOptions(OFFER_ACCEPTANCE_RATE_MAX),
    y: hiddenYScaleConfig,
  },
}));
</script>

<style lang="less" scoped>
:deep(.chart-error-image) {
  height: 137px !important;
  max-height: 137px !important;
  padding: 0px 0px 16px !important;
  margin: 0 !important;

  svg {
    margin-bottom: 16px !important;
  }
}
</style>
